import React, { useEffect, useMemo, useState } from 'react'

import { Box, Checkbox, Stack, Typography } from '@mui/material'

import { Button, Modal } from '@repo/ui'

import { Search } from 'components/input'
import { CompanyAvatar } from 'components/display/CompanyAvatar/CompanyAvatar'
import { useSnackbar } from 'contexts'
import { useCompaniesQuery, useSetMarketItemCompanies } from 'hooks/api'
import { getErrorMessage } from 'utils'
import { listedCompanies } from 'utils/listedCompany'
import type { IMarketItemCompanyRef } from 'services/marketItemsApi'

interface AssignMarketContentCompaniesModalProps {
  itemId: string
  companies: IMarketItemCompanyRef[]
  open: boolean
  onClose: () => void
}

/**
 * Replace the full set of companies a piece of content is assigned to (VRG-537
 * AC2), reusing the `AssignCompaniesModal` pattern exactly (working-conventions
 * §28) — a shared `Search` + a scrollable multi-select checklist of
 * `Checkbox` + `CompanyAvatar` rows, selection tracked as a `Set`, NOT a bare MUI
 * select. Kept as its own component rather than a generalised
 * `AssignCompaniesModal` variant: that component's mutation is hardwired to
 * `useUpdateInternalUser` (assigning a STAFFER to companies), a different
 * resource entirely from assigning CONTENT to companies here.
 */
export const AssignMarketContentCompaniesModal = ({
  itemId,
  companies,
  open,
  onClose,
}: AssignMarketContentCompaniesModalProps) => {
  const { open: notification } = useSnackbar()
  const { mutate: setCompanies, isPending } = useSetMarketItemCompanies()
  const { data: companiesData, isLoading } = useCompaniesQuery({ limit: 1000 })

  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // Snapshot of the companies assigned when the modal opened — floats current
  // assignments to the top, stable for the session (does not re-sort under the
  // cursor as you check/uncheck). See AssignCompaniesModal for the same pattern.
  const [assignedOnOpen, setAssignedOnOpen] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (open) {
      // VRG-573 renamed the ref's `id` to `companyId` (the row now has its own
      // `linkId`). Unmatched mentions have no company id and cannot be pre-ticked
      // against a list of real companies, so they filter out here.
      const ids = new Set(
        companies.map(c => c.companyId).filter((v): v is string => Boolean(v)),
      )
      setSelectedIds(ids)
      setAssignedOnOpen(ids)
      setSearch('')
    }
  }, [open, companies])

  const allCompanies = useMemo(() => {
    const list = listedCompanies(companiesData)
    return list.sort((a, b) => {
      const aRank = assignedOnOpen.has(a.id) ? 0 : 1
      const bRank = assignedOnOpen.has(b.id) ? 0 : 1
      if (aRank !== bRank) return aRank - bRank
      return (a.name ?? '').localeCompare(b.name ?? '')
    })
  }, [companiesData, assignedOnOpen])

  const filtered = useMemo(() => {
    if (!search.trim()) return allCompanies
    const q = search.toLowerCase()
    return allCompanies.filter(c => (c.name ?? '').toLowerCase().includes(q))
  }, [allCompanies, search])

  const toggle = (id: string) =>
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const handleSave = () => {
    setCompanies(
      { id: itemId, companyIds: Array.from(selectedIds) },
      {
        onSuccess: onClose,
        onError: error => notification({ type: 'error', title: getErrorMessage(error) }),
      },
    )
  }

  return (
    <Modal
      title="Assign companies"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isPending} disabled={isPending}>
            Save
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        <Search
          value={search}
          onChange={setSearch}
          onClear={() => setSearch('')}
          placeholder="Search companies"
          fullWidth
          debounce={false}
        />

        <Box
          sx={theme => ({
            maxHeight: 360,
            overflowY: 'auto',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          })}
        >
          {isLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <Typography variant="body3" color="text.secondary">
                Loading companies...
              </Typography>
            </Stack>
          ) : filtered.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <Typography variant="body3" color="text.secondary">
                {allCompanies.length === 0 ? 'No companies found.' : 'No companies match your search.'}
              </Typography>
            </Stack>
          ) : (
            filtered.map((company, index) => (
              <Stack
                key={company.id}
                direction="row"
                alignItems="center"
                spacing={2}
                onClick={() => toggle(company.id)}
                sx={theme => ({
                  px: 3,
                  py: 2,
                  cursor: 'pointer',
                  borderBottom: index < filtered.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                })}
              >
                <Checkbox
                  checked={selectedIds.has(company.id)}
                  onChange={() => toggle(company.id)}
                  onClick={e => e.stopPropagation()}
                  size="small"
                />
                <CompanyAvatar logo={company.logo} name={company.name} size={32} />
                <Typography variant="body2" noWrap>
                  {company.name}
                </Typography>
              </Stack>
            ))
          )}
        </Box>
      </Stack>
    </Modal>
  )
}
